<script>
  import Button from '@/components/ui/Button.svelte';
  import { router } from '@inertiajs/svelte'

  let { board = null, board_types = [], all_categories = [], selected_category_ids = [], flash = {} } = $props()
  // svelte-ignore state_referenced_locally
  const initialBoard = board
  // svelte-ignore state_referenced_locally
  const initialCategoryIds = selected_category_ids || []

  let form = $state({
    name: initialBoard?.name || '',
    slug: initialBoard?.slug || '',
    description: initialBoard?.description || '',
    board_type: initialBoard?.board_type || 'public',
    allow_view: initialBoard?.allow_view ?? true,
    allow_write: initialBoard?.allow_write ?? true,
    allow_anonymous: initialBoard?.allow_anonymous ?? false,
    require_moderation: initialBoard?.require_moderation ?? false,
    position: initialBoard?.position ?? 0,
    category_ids: initialCategoryIds
  })

  let isEdit = $derived(!!board?.id)
  let isSubmitting = $state(false)

  function getTypeLabel(type) {
    const labels = { public: '공개', verified_only: '인증회원 전용', admin_only: '관리자 전용' }
    return labels[type] || type
  }

  function autoSlug() {
    if (!isEdit && form.name && !form.slug) {
      form.slug = form.name
        .toLowerCase()
        .replace(/[가-힣]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
    }
  }

  function toggleCategory(categoryId) {
    const idx = form.category_ids.indexOf(categoryId)
    if (idx >= 0) {
      form.category_ids = form.category_ids.filter(id => id !== categoryId)
    } else {
      form.category_ids = [...form.category_ids, categoryId]
    }
  }

  function handleSubmit() {
    isSubmitting = true

    const options = {
      onFinish: () => {
        isSubmitting = false
      }
    }

    if (isEdit) {
      router.patch(`/admin/boards/${board.id}`, { board: form }, options)
    } else {
      router.post('/admin/boards', { board: form }, options)
    }
  }
</script>

<!-- Breadcrumb -->
  <div class="flex items-center gap-2 text-caption text-content-tertiary mb-6">
    <a href="/admin" class="hover:text-primary transition-colors">관리자</a>
    <span>/</span>
    <a href="/admin/boards" class="hover:text-primary transition-colors">게시판</a>
    <span>/</span>
    <span class="text-content">{isEdit ? '수정' : '새 게시판'}</span>
  </div>

  <main class="max-w-2xl">
    {#if flash.alert}
      <div class="mb-6 p-4 bg-error-light dark:bg-error/20 border border-error/30 rounded-xl text-error-dark dark:text-error text-body2">
        {flash.alert}
      </div>
    {/if}

    <h2 class="text-h1 text-content mb-6">{isEdit ? '게시판 수정' : '새 게시판 생성'}</h2>

    <form onsubmit={(e) => { e.preventDefault(); handleSubmit(); }} class="card card-bordered p-6 shadow-md space-y-6">
      <!-- 게시판명 -->
      <div>
        <label for="name" class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">게시판명 *</label>
        <input
          id="name"
          type="text"
          bind:value={form.name}
          oninput={autoSlug}
          required
          placeholder="예: 자유게시판"
          class="input w-full py-2.5"
        />
      </div>

      <!-- 슬러그 -->
      <div>
        <label for="slug" class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">슬러그 *</label>
        <input
          id="slug"
          type="text"
          bind:value={form.slug}
          required
          placeholder="예: free-board"
          class="input w-full py-2.5"
        />
        <p class="text-caption text-content-tertiary mt-1">URL에 사용됩니다. 영문 소문자, 숫자, 하이픈만 허용</p>
      </div>

      <!-- 설명 -->
      <div>
        <label for="description" class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">설명</label>
        <textarea
          id="description"
          bind:value={form.description}
          placeholder="게시판 설명..."
          rows="3"
          class="input w-full py-2.5"
        ></textarea>
      </div>

      <!-- 유형 -->
      <div>
        <label for="board_type" class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">게시판 유형 *</label>
        <select id="board_type" bind:value={form.board_type} class="select w-full">
          {#each board_types as type}
            <option value={type}>{getTypeLabel(type)}</option>
          {/each}
        </select>
      </div>

      <!-- 순서 -->
      <div>
        <label for="position" class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">정렬 순서</label>
        <input
          id="position"
          type="number"
          bind:value={form.position}
          class="input w-full py-2.5"
        />
        <p class="text-caption text-content-tertiary mt-1">숫자가 작을수록 먼저 표시됩니다</p>
      </div>

      <!-- 접근 제어 -->
      <div class="space-y-3">
        <p class="text-label text-gray-700 dark:text-dark-text-medium">접근 제어</p>

        <div class="flex items-center gap-3">
          <input
            id="allow_view"
            type="checkbox"
            bind:checked={form.allow_view}
            class="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <label for="allow_view" class="text-body1 text-gray-700 dark:text-dark-text-medium">
            보기 허용 (비활성화 시 사용자에게 게시판이 표시되지 않음)
          </label>
        </div>

        <div class="flex items-center gap-3">
          <input
            id="allow_write"
            type="checkbox"
            bind:checked={form.allow_write}
            class="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <label for="allow_write" class="text-body1 text-gray-700 dark:text-dark-text-medium">
            글쓰기 허용 (비활성화 시 새 게시글 작성 불가)
          </label>
        </div>
      </div>

      <!-- 옵션 -->
      <div class="space-y-3">
        <p class="text-label text-gray-700 dark:text-dark-text-medium">기타 옵션</p>

        <div class="flex items-center gap-3">
          <input
            id="allow_anonymous"
            type="checkbox"
            bind:checked={form.allow_anonymous}
            class="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <label for="allow_anonymous" class="text-body1 text-gray-700 dark:text-dark-text-medium">
            익명 게시 허용
          </label>
        </div>

        <div class="flex items-center gap-3">
          <input
            id="require_moderation"
            type="checkbox"
            bind:checked={form.require_moderation}
            class="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <label for="require_moderation" class="text-body1 text-gray-700 dark:text-dark-text-medium">
            사전 검토 필요 (게시 전 관리자 승인)
          </label>
        </div>
      </div>

      <!-- 카테고리 연결 -->
      {#if all_categories.length > 0}
        <div>
          <div class="block text-label text-gray-700 dark:text-dark-text-medium mb-2">카테고리 연결</div>
          <div class="flex flex-wrap gap-2">
            {#each all_categories as category}
              <button
                type="button"
                onclick={() => toggleCategory(category.id)}
                class="px-3 py-1.5 rounded-lg text-body2 border transition-colors
                  {form.category_ids.includes(category.id)
                    ? 'bg-primary text-white border-primary'
                    : 'bg-surface text-content-secondary border-gray-300 dark:border-dark-border hover:border-primary'
                  }"
              >{category.name}</button>
            {/each}
          </div>
        </div>
      {/if}

      <!-- Submit -->
      <div class="flex items-center justify-end gap-3 pt-4 border-t border-default">
        <a href="/admin/boards" class="btn btn-secondary btn-md">취소</a>
        <Button type="submit" size="md" loading={isSubmitting} loadingLabel={isEdit ? '수정 중...' : '생성 중...'} disabled={!form.name || !form.slug}>
          {isEdit ? '수정' : '생성'}
        </Button>
      </div>
    </form>
  </main>
